import { nowUTC } from "@homebound/activesupport";
import {
  Button,
  ButtonMenu,
  Chip,
  Css,
  DateRangeFilterValue,
  FilterDefs,
  Filters,
  OffsetAndLimit,
  dateRangeFilter,
  multiFilter,
  singleFilter,
  useComputed,
  useGridTableApi,
  useModal,
  usePersistedFilter,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { SearchBox } from "src/components";
import { InternalUserDetailFragment } from "src/fragments/circularFragments.generated";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import {
  DateOperation,
  InputMaybe,
  LotType,
  WarrantyTicketFilter,
  WarrantyTicketItemStatus,
  WarrantyTicketStatus,
} from "src/generated/graphqlTypes";
import useZodQueryString from "src/hooks/useZodQueryString";
import { useCurrentInternalUserQuery } from "src/queries/user.generated";
import {
  WarrantyTicketDetailsFragment,
  WarrantyTicketItemDetailsFragment,
  WarrantyTicketItemStatusesFragment,
  WarrantyTicketStatusesFragment,
  useSaveWarrantyTicketItemsMutation,
  useWarrantyTicketFiltersQuery,
  useWarrantyTicketsQuery,
} from "src/routes/warranty/WarrantyPage.generated";
import { WarrantyTicketPageDetailsFragment } from "src/routes/warranty/WarrantyTicketPage.generated";
import { queryResult, searchSchema } from "src/utils";
import { hasCompleteDateRange } from "src/utils/temporal";
import { PageHeader } from "../layout/PageHeader";
import { TableActions } from "../layout/TableActions";
import { WarrantyPageTable, WarrantyRow } from "./WarrantyPageTable";
import { ChangeStatusModal } from "./components/ChangeStatusModal";
import { CreateWarrantyTicketModal } from "./components/CreateWarrantyTicketModal";

const defaultPageOptions: OffsetAndLimit = { limit: 50, offset: 0 };

export function WarrantyPage() {
  const result = useWarrantyTicketFiltersQuery();
  return queryResult(
    result,
    ({ warrantyTicketItemStatusesDetail, warrantyTicketStatusesDetail, markets, internalUsers }) => (
      <WarrantyDataView
        warrantyTicketItemStatusesDetail={warrantyTicketItemStatusesDetail}
        warrantyTicketStatusesDetail={warrantyTicketStatusesDetail}
        markets={markets}
        teamMembers={internalUsers}
      />
    ),
  );
}

type WarrantyDataViewProps = {
  warrantyTicketItemStatusesDetail: WarrantyTicketItemStatusesFragment[];
  warrantyTicketStatusesDetail: WarrantyTicketStatusesFragment[];
  markets: NamedFragment[];
  teamMembers: InternalUserDetailFragment[];
};

function WarrantyDataView(props: WarrantyDataViewProps) {
  const { warrantyTicketItemStatusesDetail, warrantyTicketStatusesDetail, markets, teamMembers } = props;
  const { openModal } = useModal();
  const [saveWarrantyTicketItems] = useSaveWarrantyTicketItemsMutation();
  const [{ search }, setSearch] = useZodQueryString(searchSchema);
  const tableApi = useGridTableApi<WarrantyRow>();
  const selectedWarrantyTicketItemIds = useComputed(
    () => tableApi.getSelectedRows("warrantyTicketItem").map((r) => r.data.wti.id),
    [tableApi],
  );

  /** A callback function to avoid re-render the component when use setSearch directly */
  const searchCallback = useCallback((text: string) => setSearch({ search: text }), [setSearch]);

  const { data: currentInternalUserData } = useCurrentInternalUserQuery({ fetchPolicy: "cache-first" });
  const currentUser = currentInternalUserData?.currentInternalUser;

  const filterDefs: FilterDefs<WarrantyFilter> = useMemo(() => {
    const itemStatuses = multiFilter({
      label: "Item Status",
      options: warrantyTicketItemStatusesDetail,
      getOptionValue: (o) => o.code,
      getOptionLabel: (o) => o.name,
    });
    const marketIds = multiFilter({
      label: "Market",
      options: markets,
      getOptionValue: (o) => o.id,
      getOptionLabel: (o) => o.name,
    });

    const urgent = singleFilter({
      label: "Urgency",
      options: [
        { label: "Urgent", value: "true" },
        { label: "Not Urgent", value: "false" },
      ],
      getOptionValue: (o) => o.value,
      getOptionLabel: (o) => o.label,
    });

    const projectLotType = multiFilter({
      options: Object.values(LotType),
      getOptionValue: (v) => v,
      getOptionLabel: (v) => v,
    });

    const statuses = multiFilter({
      label: "Ticket Status",
      options: warrantyTicketStatusesDetail,
      getOptionValue: (o) => o.code,
      getOptionLabel: (o) => o.name,
      defaultValue: [WarrantyTicketStatus.NotStarted, WarrantyTicketStatus.InProgress],
    });

    const currentTeamMember = teamMembers.find((tm) => tm.id === currentUser?.id);
    const otherTeamMembers = teamMembers.filter((tm) => tm.id !== currentUser?.id);
    const teamMembersOptions = currentTeamMember ? [currentTeamMember, ...otherTeamMembers] : teamMembers;

    const assigneeIds = multiFilter({
      label: "Assigned to",
      options: teamMembersOptions,
      getOptionValue: (o) => o.id,
      getOptionLabel: ({ name, id }) => (id === currentUser?.id ? `${name} (Me)` : name),
      defaultValue: currentTeamMember ? [currentTeamMember.id] : undefined,
    });

    const itemCreatedDateRange = dateRangeFilter({ label: "Date submitted" });

    return { itemStatuses, marketIds, urgent, itemCreatedDateRange, projectLotType, statuses, assigneeIds };
  }, [currentUser, warrantyTicketItemStatusesDetail, markets, warrantyTicketStatusesDetail, teamMembers]);

  const { filter, setFilter } = usePersistedFilter<WarrantyFilter>({ storageKey: "warranty-filter", filterDefs });
  const itemCreatedDateRange = hasCompleteDateRange(filter.itemCreatedDateRange?.value)
    ? {
        op: DateOperation.Between,
        value: filter.itemCreatedDateRange.value.from,
        value2: filter.itemCreatedDateRange.value.to,
      }
    : undefined;

  const query = useWarrantyTicketsQuery({
    variables: {
      page: defaultPageOptions,
      filter: { ...filter, search, itemCreatedDateRange, urgent: filter.urgent ? filter.urgent === "true" : undefined },
      statuses: filter.itemStatuses,
      itemCreatedDateRange,
    },
  });

  return queryResult(query, {
    data: ({ warrantyTicketsPage }) => {
      return (
        <>
          <PageHeader
            title="Warranty"
            right={
              <Button
                label="Create New"
                onClick={() => openModal({ content: <CreateWarrantyTicketModal />, size: "md" })}
              />
            }
          />
          <TableActions>
            <Filters filter={filter} onChange={setFilter} filterDefs={filterDefs} numberOfInlineFilters={2} />
            <div css={Css.mla.df.gap1.$}>
              <SearchBox onSearch={searchCallback} />
              <ButtonMenu
                items={[
                  {
                    label: "Change Status",
                    disabled: selectedWarrantyTicketItemIds.length === 0,
                    onClick: async () =>
                      openModal({
                        content: (
                          <ChangeStatusModal
                            warrantyTicketItemStatusesDetail={warrantyTicketItemStatusesDetail}
                            onConfirmAction={async (status) => {
                              await saveWarrantyTicketItems({
                                variables: {
                                  input: {
                                    warrantyTicketItems: selectedWarrantyTicketItemIds.map((id) => ({ id, status })),
                                  },
                                },
                              });
                              tableApi.clearSelections();
                            }}
                          />
                        ),
                      }),
                  },
                ]}
                trigger={{ label: "Actions All" }}
              />
            </div>
          </TableActions>
          <WarrantyPageTable
            warrantyTickets={warrantyTicketsPage.entities}
            tableApi={tableApi}
            warrantyTicketItemStatusesDetail={warrantyTicketItemStatusesDetail}
            teamMembers={teamMembers}
            pageOptions={{
              hasNextPage: warrantyTicketsPage.pageInfo.hasNextPage,
              fetchMore: () =>
                query.fetchMore({
                  variables: {
                    page: {
                      ...defaultPageOptions,
                      limit: warrantyTicketsPage.entities.length + defaultPageOptions.limit,
                    },
                  },
                }),
            }}
          />
        </>
      );
    },
  });
}

type WarrantyFilter = Omit<
  WarrantyTicketFilter,
  "projectId" | "homeownerVisible" | "urgent" | "itemCreatedDateRange"
> & { urgent: InputMaybe<string>; itemCreatedDateRange: DateRangeFilterValue<string> };

export function getWarrantyItemDurationChip(wti: WarrantyTicketItemDetailsFragment) {
  // check wti item exist
  if (!wti || !wti.statusUpdatedAt) {
    return undefined;
  }
  // if completed or reject return undefined
  if (wti.status.code === WarrantyTicketItemStatus.Completed || wti.status.code === WarrantyTicketItemStatus.Rejected) {
    return undefined;
  }
  // find the diff time last updated and current date
  const diffDays = wti.statusUpdatedAt.until(nowUTC(), { largestUnit: "day" }).days;
  // if current status Submitted, and accepted have the same colors
  if (wti.status.code === WarrantyTicketItemStatus.Submitted || wti.status.code === WarrantyTicketItemStatus.Accepted) {
    const chipType = () => {
      if (diffDays <= 1) {
        return "success";
      } else if (diffDays <= 3) {
        return "caution";
      } else {
        return "warning";
      }
    };

    return { content: () => <Chip text={`${diffDays}d`} type={chipType()} />, sortValue: diffDays };
  } else if (
    wti.status.code === WarrantyTicketItemStatus.OnHold ||
    wti.status.code === WarrantyTicketItemStatus.Scheduled
  ) {
    const chipType = () => {
      if (diffDays <= 7) {
        return "success";
      } else if (diffDays <= 14) {
        return "caution";
      } else {
        return "warning";
      }
    };
    return { content: () => <Chip text={`${diffDays}d`} type={chipType()} />, sortValue: diffDays };
  }
  return undefined;
}

export function getWarrantyTicketDurationChip(wt: WarrantyTicketDetailsFragment | WarrantyTicketPageDetailsFragment) {
  // if no items return undefined
  if (wt.items.length === 0) {
    return undefined;
  }
  // check if all items are completed or rejected then the duration will be difference between created and last updated
  if (
    wt.items.every(
      (item) =>
        item.status.code === WarrantyTicketItemStatus.Completed ||
        item.status.code === WarrantyTicketItemStatus.Rejected,
    )
  ) {
    // find the last updated item
    const lastUpdatedItem = wt.items.reduce((prev, current) =>
      prev.statusUpdatedAt.isAfter(current.statusUpdatedAt) ? prev : current,
    );
    const diffDays = wt.createdAt.until(lastUpdatedItem.statusUpdatedAt, { largestUnit: "day" }).days;
    return { content: () => <Chip text={`${diffDays}d`} type="success" />, sortValue: diffDays };
  }
  // else the duration will be difference between created and current date
  const diffDays = wt.createdAt.until(nowUTC(), { largestUnit: "day" }).days;
  return { content: () => <Chip text={`${diffDays}d`} type="caution" />, sortValue: diffDays };
}
